import { TrustGauge } from '../trust-gauge/TrustGauge.tsx'
import type { IndexCompetition, IndexItem } from './types'
import { IndexTile, StatusBadge } from './IndexRow'
import { KIND_LABELS, followLink, formatLabel, itemSubtitle, prizeLabel, teamsLabel, tileName } from './view'
import styles from './IndexSearch.module.css'

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

type Fact = [label: string, value: string]

function factsFor(item: IndexItem): Fact[] {
  switch (item.kind) {
    case 'competition': {
      const facts: Fact[] = [
        ['Game', item.gameName],
        ['Format', formatLabel(item.format)],
        ['Starts', item.startsAt ? longDate(item.startsAt) : 'TBD'],
        ['Teams', teamsLabel(item)],
      ]
      if (item.prizePool > 0) facts.push(['Prize pool', prizeLabel(item.prizePool)])
      return facts
    }
    case 'team':
      return [
        ['Club', item.orgName],
        ['Game', item.gameName],
        ['Roster', `${item.memberCount} ${item.memberCount === 1 ? 'player' : 'players'}`],
      ]
    case 'player':
      return item.teamName ? [['Team', item.teamName]] : []
    default:
      return [
        ['Teams', String(item.teamCount)],
        ['Competitions', String(item.competitionCount)],
      ]
  }
}

/** The verb on a competition depends on what you can do with it right now. */
function competitionAction(c: IndexCompetition): string {
  if (c.phase === 'registration_open') return 'Register a team'
  if (c.phase === 'live') return 'Watch live'
  return 'Open competition'
}

function primaryAction(item: IndexItem): string {
  switch (item.kind) {
    case 'competition':
      return competitionAction(item)
    case 'player':
      return 'View profile'
    case 'team':
      return 'Open team'
    default:
      return `Open ${KIND_LABELS[item.kind].toLowerCase()}`
  }
}

export interface IndexPreviewProps {
  item: IndexItem
  onNavigate: (href: string) => void
}

/**
 * A closer look at the highlighted result, without leaving the Index. Built
 * only from what the result already carries — no second request.
 */
export function IndexPreview({ item, onNavigate }: IndexPreviewProps) {
  const facts = factsFor(item)
  return (
    <aside className={styles.preview} aria-label={`Preview: ${item.name}`}>
      <div className={styles.previewTab}>
        {tileName(item).charAt(0).toUpperCase()} <span aria-hidden="true">·</span> {KIND_LABELS[item.kind]}
      </div>
      <div className={styles.previewCard}>
        <div className={styles.previewBand} style={item.color ? { background: item.color } : undefined} />
        <div className={styles.previewBody}>
          <div className={styles.previewHead}>
            <IndexTile name={tileName(item)} logoUrl={item.logoUrl} color={item.color} round={item.kind === 'player'} size="lg" />
            <div className={styles.previewTitle}>
              <span className={styles.previewName}>{item.name}</span>
              <span className={styles.rowSub}>{itemSubtitle(item)}</span>
            </div>
          </div>

          {item.kind === 'competition' && <StatusBadge competition={item} />}
          {item.kind === 'player' && (item.isGuest ? (
            <p className={styles.previewNote}>Guest player — added by an organizer, not yet claimed.</p>
          ) : item.trustScore != null ? (
            <div className={styles.previewTrust}>
              <TrustGauge score={item.trustScore} size={56} />
              <span className={styles.rowSub}>Trust · {item.trustLevel || 'Unverified'}</span>
            </div>
          ) : null)}
          {item.kind !== 'player' && item.kind !== 'team' && item.kind !== 'competition' && item.relatedVia && (
            <p className={styles.previewNote}>Shown because its team {item.relatedVia} matched.</p>
          )}

          {facts.length > 0 && (
            <dl className={styles.facts}>
              {facts.map(([label, value]) => (
                <div key={label} className={styles.fact}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className={styles.previewActions}>
            <a className={styles.primaryBtn} href={item.href} onClick={(e) => followLink(e, item.href, onNavigate)}>
              {primaryAction(item)}
            </a>
            {item.kind === 'team' && (
              <a
                className={styles.secondaryBtn}
                href={item.orgHref}
                onClick={(e) => followLink(e, item.orgHref, onNavigate)}
              >
                Open club
              </a>
            )}
          </div>
        </div>
      </div>
    </aside>
  )
}
